import { Box, Typography } from "@mui/material";
import { ProjectResponse, useGetApiProjectsByProjectIdFoldersQuery } from "api/generatedApi";
import { ExtendedProjectFolder, useMapTreeToFlat } from "pages/project/hooks/use-map-tree-to-flat";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "shared/toast/hooks/use-toast";
import { LocationCityOutlined } from "@mui/icons-material";

interface Props {
  project: ProjectResponse;
  onSubmit: (project: ProjectResponse, dataFlatlist: ExtendedProjectFolder[]) => void;
  isSelected?: boolean;
}

export const ProjectsItem = (props: Props) => {
  const { project, isSelected } = props;
  const { t } = useTranslation();
  const tRef = useRef(t);
  const toast = useToast();
  const toastRef = useRef(toast);
  const { mapTreeToFlat, mapTreeNode } = useMapTreeToFlat();
  const { data: rootFolder, isError: getFolderHasError } = useGetApiProjectsByProjectIdFoldersQuery(
    {
      projectId: project.projectId ?? "", //caches api call and only calls the api if the projectId changes
    },
    {
      skip: !project.projectId,
    }
  );

  //useMemo is a React hook that memoizes (caches) the result of an expensive calculation and only recalculates it when specific dependencies change.
  const dataFlatlist = useMemo(() => {
    if (!rootFolder?.rootFolder) {
      return undefined;
    }

    const root = mapTreeNode(rootFolder.rootFolder, undefined, true);
    root.projectFolderName = project?.projectName;

    const result = !root?.subFolders ? undefined : mapTreeToFlat(root.subFolders, root);
    result?.flatlist?.push(root);
    root.flatlist = result?.flatlist;

    return result;
  }, [rootFolder, project?.projectName, mapTreeToFlat, mapTreeNode]);

  useEffect(() => {
    if (getFolderHasError) {
      toastRef.current.error(tRef.current("folder.menu.getRootError", { project: project.projectName }));
    }
  }, [getFolderHasError, toastRef, tRef]);

  const handleCClick = () => {
    if (project && dataFlatlist?.flatlist) {
      props.onSubmit(project, dataFlatlist.flatlist);
    }
  };

  //the styling used is taken from the tree component

  return (
    <Box
      sx={{
        cursor: "pointer",
        padding: "8px",
        borderRadius: "4px",
        borderColor: isSelected ? "primary.main" : "divider",
        backgroundColor: isSelected ? "action.selected" : "transparent",
        "&:hover": {
          backgroundColor: isSelected ? "action.selected" : "action.hover",
          borderColor: "primary.main",
        },
        marginBottom: "4px",
      }}
      onClick={handleCClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          paddingY: "2px",
        }}
      >
        <LocationCityOutlined color="primary" />

        <Box sx={{ paddingLeft: "10px", flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" color="primary.main" title={project.projectName ?? ""} noWrap>
            {project.projectName}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

//multiple steps have been taken in the class to limit unnecessary re-calculations and api calls on every render, including the use of useMemo for data transformation and selective API calls based on projectId.
//because a user can have a lot of projects.
